import React from "react";
import styled, { keyframes } from "styled-components/macro";
import { Stack, Typography } from "@mui/material";
import { BubbleWrap, Bubble } from "./ChatBubble";
import { Trans } from "react-i18next";
import { ideasConfig, ChatIdeasList, useChatIdeasSettings } from "hooks/useChatIdeas";
import { AssistantMode } from "components/assistant/__generated__/AssistantMutation.graphql";
import { containsSmallAlphabetChars, SMALL_ALPHABET_FONT_SIZE } from "tools/smallAlphabetDetection";

const IdeasContainer = styled.div`
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const IdeasHeading = styled(Typography)`
  font-size: 11px;
  color: ${(p) => p.theme.duo.palette.greyDark};
  margin-bottom: 8px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.6;
`;

const IdeasBubbleWrap = styled(BubbleWrap)`
  justify-content: flex-start;
`;

const IdeasBubble = styled(Bubble)<{ $disabled?: boolean }>`
  border: 1px solid ${(p) => p.theme.duo.palette.greyLight};
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    transform 0.02s ease;
  color: ${(p) => p.theme.duo.color.text};
  font-weight: 400;
  padding: 12px 16px;
  border-radius: 12px;
  pointer-events: ${(p) => (p.$disabled ? "none" : "auto")};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  font-size: 14px;

  &:hover {
    border-color: ${(p) => p.theme.duo.palette.blueMediumLight};
  }
`;

const IdeaText = styled.div<{ $hasSmallAlphabet?: boolean }>`
  ${(p) => p.$hasSmallAlphabet && `font-size: ${SMALL_ALPHABET_FONT_SIZE};`}
  line-height: 1.35;
  color: ${(p) => p.theme.duo.color.text};
`;

const IdeaTranslation = styled.div`
  font-size: 13px;
  margin-top: 4px;
  line-height: 1.35;
  color: ${(p) => p.theme.duo.palette.greyDark};
  opacity: 0.9;
`;

const pulse = keyframes`
  0% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 0.4;
  }
`;

const PlaceholderBubble = styled(IdeasBubble)`
  pointer-events: none;
  animation: ${pulse} 1.5s ease-in-out infinite;
  border: none;
  padding-right: 0;
`;

const PlaceholderLine = styled.div<{ $size?: "small" | "large" | "translation" }>`
  height: 14px;
  border-radius: 8px;
  background: ${(p) => p.theme.duo.color.grey};
  opacity: 0.4;
  width: ${(p) => {
    switch (p.$size) {
      case "large":
        return "200px";
      case "translation":
        return "140px";
      default:
        return "160px";
    }
  }};
  max-width: 100%;
  margin-top: ${(p) => (p.$size === "translation" ? "4px" : "0")};
`;

export enum IdeaTag {
  CORRECT = "correct",
  WRONG = "wrong"
}

type ChatIdeasProps = {
  ideas: ChatIdeasList;
  loading: boolean;
  mode: AssistantMode;
  onIdeaClick?: (idea: string, tags?: string[]) => void;
};

const ChatIdeas: React.FC<ChatIdeasProps> = ({ ideas, onIdeaClick, loading, mode }) => {
  const [settings] = useChatIdeasSettings({ mode });
  const [isProcessing, setIsProcessing] = React.useState(false);
  const showIdeas = settings.active;
  const showTranslations = settings.withTranslation;

  // Reset processing state when ideas change or loading starts
  React.useEffect(() => {
    if (loading || ideas.length === 0) {
      setIsProcessing(false);
    }
  }, [loading, ideas]);

  const handleIdeaClick = (idea: string, tags?: string[]) => {
    if (isProcessing) return;
    setIsProcessing(true);
    if (onIdeaClick) {
      onIdeaClick(idea, tags);
    }
  };

  const supportsIdeas = Boolean(ideasConfig[mode]) && showIdeas;
  const shouldRenderIdeasSection = supportsIdeas && (loading || ideas.length > 0);

  if (!shouldRenderIdeasSection) return null;

  return (
    <IdeasContainer>
      <IdeasHeading variant="body2">
        <Trans>{ideasConfig[mode]?.label || "ideas"}</Trans>
      </IdeasHeading>
      <Stack spacing={1} alignItems="flex-end">
        {loading ? (
          <>
            {Array.from({ length: mode === "letsPractise" ? 3 : 2 }, (_, placeholderIndex) => (
              <IdeasBubbleWrap key={`placeholder-${placeholderIndex}`} $owner="user">
                <PlaceholderBubble $owner="user">
                  <Stack direction="column" spacing={2} alignItems="flex-end">
                    <PlaceholderLine $size={placeholderIndex % 2 === 0 ? "small" : "large"} />
                    {showTranslations && <PlaceholderLine $size="translation" />}
                  </Stack>
                </PlaceholderBubble>
              </IdeasBubbleWrap>
            ))}
          </>
        ) : (
          ideas.map((i) => {
            const key = `${i.idea}__${i.ideaTranslation}`;
            const hasSmallAlphabet = containsSmallAlphabetChars(i.idea || "");
            return (
              <IdeasBubbleWrap key={key} $owner="user">
                <IdeasBubble
                  $owner="user"
                  $disabled={isProcessing}
                  onClick={() =>
                    handleIdeaClick(
                      i.idea,
                      i.tags?.filter((t): t is string => t !== null)
                    )
                  }
                >
                  <Stack direction="column" spacing={0} alignItems="flex-start">
                    <IdeaText $hasSmallAlphabet={hasSmallAlphabet}>{i.idea}</IdeaText>
                    {showTranslations && <IdeaTranslation>{i.ideaTranslation}</IdeaTranslation>}
                  </Stack>
                </IdeasBubble>
              </IdeasBubbleWrap>
            );
          })
        )}
      </Stack>
    </IdeasContainer>
  );
};

export default ChatIdeas;
